// G3 consent banner: preview build trigger
'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { gpcFromNavigator, parseConsentCookie, type ConsentState } from '@/lib/identity/consent'
import { consentRegionRestrictedFromCookieHeader } from '@/lib/analytics/consent-regions'
import { CLARITY_ANALYTICS_OFF_EVENT } from '@/lib/analytics/clarity'
import { cogLift } from '@/components/cookie-cog-placement'
import { COOKIE_BAR_CLEAR_ATTR, cookieBarMaxHeight, rootPx } from '@/components/cookie-bar-fit'
import { HugeiconsIcon } from '@hugeicons/react'
import { Settings01Icon } from '@hugeicons/core-free-icons'
import { applyConsentGrant, applyConsentOptOut } from '@/lib/analytics/consent-withdraw'
import { BANNER_RESTRICTED, BANNER_US, CHOICES_PANEL } from '@/components/privacy/consent-copy'
import { PrivacyChoicesPanel } from '@/components/privacy/PrivacyChoicesPanel'
import {
  CONSENT_PROMPT_SESSION_KEY,
  CONSENT_PROMPT_STORAGE_KEY,
  CONSENT_PURPOSES_VERSION,
  CONTEXTUAL_CONSENT_ASK_EVENT,
  OPEN_COOKIE_SETTINGS_EVENT,
  consentFirstLayerSuppressed,
  parseConsentDecisionMeta,
  parsePromptBookkeeping,
  serializePromptBookkeeping,
  shouldShowConsentPrompt,
  shouldShowContextualConsentAsk,
  type ConsentPromptStored,
  type PromptBookkeeping,
} from '@/lib/identity/consent-prompt'

export {
  CONSENT_PURPOSES_VERSION,
  CONTEXTUAL_CONSENT_ASK_EVENT,
  OPEN_COOKIE_SETTINGS_EVENT,
  shouldShowConsentPrompt,
} from '@/lib/identity/consent-prompt'

const COOKIE_CONSENT_KEY = 'ryan_realty_cookie_consent'
const CONSENT_EXPIRY_YEARS = 1

/** Shared size so the two answers match in height and width (G3: Decline equals Accept). */
export const CONSENT_CHOICE_BUTTON_CLASS = 'h-11 min-h-11 w-full min-w-0 px-3'

export type { ConsentState }

/** The stored consent choice, or null when the visitor has not answered the
 *  banner yet. Callers that need to distinguish "no choice" (functional
 *  essential tracking allowed) from an explicit decline use this instead of
 *  the boolean getters. */
export function getStoredConsent(): ConsentState | null {
  return getConsent()
}

/** The banner's cookie value as the browser holds it, undecoded; undefined when there is none. */
function readConsentCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(COOKIE_CONSENT_KEY + '='))
    ?.split('=')[1]
}

function getConsent(): ConsentState | null {
  return parseConsentCookie(readConsentCookie())
}

function readPromptStored(): ConsentPromptStored {
  const raw = readConsentCookie()
  const state = parseConsentCookie(raw)
  if (!state) return null
  const { v } = parseConsentDecisionMeta(raw)
  return v == null ? state : { ...state, v }
}

function gpcNow(): boolean {
  return gpcFromNavigator(typeof navigator !== 'undefined' ? navigator : undefined)
}

function setConsentState(state: ConsentState, gpcAnalytics = false) {
  const expires = new Date() // hydration-safe: cookie write on click, not render
  expires.setFullYear(expires.getFullYear() + CONSENT_EXPIRY_YEARS)
  const now = Date.now() // hydration-safe: cookie write on click, not render
  const payload = {
    analytics: state.analytics,
    marketing: state.marketing,
    ts: now,
    at: new Date(now).toISOString(), // hydration-safe: cookie write on click, not render
    v: CONSENT_PURPOSES_VERSION,
    ...(gpcAnalytics ? { gpcAnalytics: true } : {}),
  }
  document.cookie = `${COOKIE_CONSENT_KEY}=${encodeURIComponent(JSON.stringify(payload))}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`
}

export function hasTrackingConsent(): boolean {
  const c = getConsent()
  return c !== null && c.analytics && c.marketing
}

/**
 * G3: US (rr_cr=0) with no answer is on; restricted or unknown region is off
 * until a yes. GPC turns analytics off unless the visitor turned it on in Your
 * privacy choices while the signal was on (gpcAnalytics in the cookie).
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false
  const c = getConsent()
  if (gpcNow()) return c !== null && c.analytics && parseConsentDecisionMeta(readConsentCookie()).gpcAnalytics
  if (c !== null) return c.analytics
  return !consentRegionRestrictedFromCookieHeader(document.cookie)
}

/** Ads need an explicit yes everywhere, and never under GPC. */
export function hasMarketingConsent(): boolean {
  if (typeof window === 'undefined') return false
  if (gpcNow()) return false
  const c = getConsent()
  return c !== null && c.marketing
}

/**
 * Store an answer and apply it in the page now (G3). Used by the banner, its
 * Choices dialog and the /privacy-choices page. Under GPC, ads stay off and an
 * analytics yes is marked gpcAnalytics so the signal does not undo it.
 * Returns what was stored.
 */
export function saveConsentChoice(choice: { analytics: boolean; marketing: boolean }): ConsentState {
  const gpc = gpcNow()
  const saved: ConsentState = { analytics: choice.analytics, marketing: gpc ? false : choice.marketing }
  setConsentState(saved, gpc && saved.analytics)
  if (!saved.analytics) {
    applyConsentOptOut(window, document)
    if (saved.marketing) applyConsentGrant(window, saved)
  } else {
    applyConsentGrant(window, saved, document)
  }
  window.dispatchEvent(
    new CustomEvent('cookie-consent', { detail: saved.analytics && saved.marketing ? 'all' : 'essential' }),
  )
  return saved
}

export function getOrCreateVisitId(): string | null {
  if (typeof document === 'undefined') return null
  const name = 'ryan_realty_visit_id'
  const existing = document.cookie
    .split('; ')
    .find((row) => row.startsWith(name + '='))
    ?.split('=')[1]
  if (existing) return existing
  const id = crypto.randomUUID?.() ?? `v_${Date.now()}_${Math.random().toString(36).slice(2)}` // hydration-safe: cookie write, not render
  const expires = new Date() // hydration-safe: cookie write, not render
  expires.setFullYear(expires.getFullYear() + 1)
  document.cookie = `${name}=${id}; path=/; expires=${expires.toUTCString()}; SameSite=Lax`
  return id
}

function readBookkeeping(): PromptBookkeeping {
  try {
    return parsePromptBookkeeping(localStorage.getItem(CONSENT_PROMPT_STORAGE_KEY))
  } catch {
    return { lastShownAt: null, contextualAskUsed: false }
  }
}

function writeBookkeeping(next: PromptBookkeeping) {
  try {
    localStorage.setItem(CONSENT_PROMPT_STORAGE_KEY, serializePromptBookkeeping(next))
  } catch {
    /* private mode */
  }
}

export default function CookieConsentBanner() {
  const pathname = usePathname()
  const chromeHidden = consentFirstLayerSuppressed(pathname)
  const [surface, setSurface] = useState<'hidden' | 'bar'>('hidden')
  const [prefsOpen, setPrefsOpen] = useState(false)
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(false)
  const [restricted, setRestricted] = useState(true)
  const [gpcOn, setGpcOn] = useState(false)
  const [hasChoice, setHasChoice] = useState(false)
  const promptRef = useRef({
    stored: null as ConsentPromptStored,
    gpc: false,
    restricted: true,
    contextualAskUsed: false,
  })
  const shownMarked = useRef(false)
  const openChooserRef = useRef<() => void>(() => {})

  useEffect(() => {
    const gpc = gpcFromNavigator(typeof navigator !== 'undefined' ? navigator : undefined)
    const regionRestricted = consentRegionRestrictedFromCookieHeader(document.cookie)
    const stored = readPromptStored()
    const book = readBookkeeping()
    let newSession = true
    try {
      newSession = sessionStorage.getItem(CONSENT_PROMPT_SESSION_KEY) !== '1'
      sessionStorage.setItem(CONSENT_PROMPT_SESSION_KEY, '1')
    } catch {
      /* private mode */
    }

    setGpcOn(gpc)
    setRestricted(regionRestricted)
    promptRef.current = {
      stored,
      gpc,
      restricted: regionRestricted,
      contextualAskUsed: book.contextualAskUsed,
    }

    if (stored !== null) {
      setAnalytics(gpc ? hasAnalyticsConsent() : stored.analytics)
      setMarketing(gpc ? false : stored.marketing)
      setHasChoice(true)
    } else {
      setAnalytics(gpc ? false : !regionRestricted)
      setMarketing(false)
    }

    const now = Date.now() // hydration-safe: mount effect, not render
    const mayPrompt = shouldShowConsentPrompt({
      stored,
      lastShownAt: book.lastShownAt,
      newSession,
      gpc,
      now,
    })

    const onConsent = () => {
      const next = readPromptStored()
      promptRef.current.stored = next
      if (next) {
        setAnalytics(promptRef.current.gpc ? hasAnalyticsConsent() : next.analytics)
        setMarketing(promptRef.current.gpc ? false : next.marketing)
        setHasChoice(true)
      }
      setSurface('hidden')
    }
    window.addEventListener('cookie-consent', onConsent)

    // preventDefault tells CookieSettingsLink the dialog opened, so it does not
    // also follow its /privacy#choices fallback.
    const onOpenSettings = (event: Event) => {
      event.preventDefault()
      promptRef.current.stored = readPromptStored()
      openChooserRef.current()
    }
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpenSettings)

    const onContextual = () => {
      const snap = promptRef.current
      if (
        !shouldShowContextualConsentAsk({
          stored: snap.stored,
          gpc: snap.gpc,
          restricted: snap.restricted,
          contextualAskUsed: snap.contextualAskUsed,
        })
      ) {
        return
      }
      const nextBook = { ...readBookkeeping(), contextualAskUsed: true, lastShownAt: Date.now() } // hydration-safe: event handler
      writeBookkeeping(nextBook)
      promptRef.current.contextualAskUsed = true
      shownMarked.current = true
      setSurface('bar')
    }
    window.addEventListener(CONTEXTUAL_CONSENT_ASK_EVENT, onContextual)

    if (mayPrompt) setSurface('bar')

    return () => {
      window.removeEventListener('cookie-consent', onConsent)
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpenSettings)
      window.removeEventListener(CONTEXTUAL_CONSENT_ASK_EVENT, onContextual)
    }
  }, [])

  useEffect(() => {
    if (surface !== 'bar' || chromeHidden) return
    if (shownMarked.current) return
    shownMarked.current = true
    const now = Date.now() // hydration-safe: effect after reveal, not render
    writeBookkeeping({ ...readBookkeeping(), lastShownAt: now })
  }, [surface, chromeHidden])

  function currentToggles(): { analytics: boolean; marketing: boolean } {
    const snap = promptRef.current
    if (snap.gpc) return { analytics: hasAnalyticsConsent(), marketing: false }
    if (snap.stored) return { analytics: snap.stored.analytics, marketing: snap.stored.marketing }
    return { analytics: !snap.restricted, marketing: false }
  }

  function answer(choice: { analytics: boolean; marketing: boolean }) {
    const saved = saveConsentChoice(choice)
    setAnalytics(saved.analytics)
    setMarketing(saved.marketing)
    setHasChoice(true)
    promptRef.current.stored = { ...saved, v: CONSENT_PURPOSES_VERSION }
    setSurface('hidden')
    setPrefsOpen(false)
  }

  function openChooser() {
    const toggles = currentToggles()
    setAnalytics(toggles.analytics)
    setMarketing(toggles.marketing)
    setPrefsOpen(true)
  }
  openChooserRef.current = openChooser

  const copy = restricted ? BANNER_RESTRICTED : BANNER_US
  const acceptVariant = restricted ? 'secondary' : 'default'
  const declineVariant = 'secondary' as const
  const showIcon = hasChoice && surface === 'hidden' && !prefsOpen && !chromeHidden
  const cogRef = useRef<HTMLButtonElement>(null)
  const [cogLiftPx, setCogLiftPx] = useState<number | null>(0)
  const cogLiftRef = useRef<number | null>(0)
  useEffect(() => {
    if (!showIcon) return
    let frame = 0
    const place = () => {
      frame = 0
      const el = cogRef.current
      if (!el) return
      const next = cogLift(document, el, cogLiftRef.current ?? 0)
      if (next !== cogLiftRef.current) {
        cogLiftRef.current = next
        // Write the lift in this frame (Public Look recheck on PR 859: the React
        // state round trip let the cog sit on text until scrolling settled).
        el.style.transform = next ? `translateY(-${next}px)` : ''
        el.style.visibility = next === null ? 'hidden' : ''
        setCogLiftPx(next)
      }
    }
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(place)
    }
    const settle = window.setTimeout(schedule, 600)
    schedule()
    // Scroll runs the check synchronously so the frame that paints the new
    // scroll position already has the cog clear of text.
    const onScroll = () => {
      if (frame) {
        window.cancelAnimationFrame(frame)
        frame = 0
      }
      place()
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      window.clearTimeout(settle)
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', schedule)
    }
  }, [showIcon])
  const showFirstLayer = !gpcOn && !chromeHidden && !prefsOpen && surface === 'bar'

  // The phone dock and sticky ask sit above this bar via --v3-cookie-bar-h.
  // The old fallback (5.5rem) is shorter than this sheet, so they landed on Decline.
  useEffect(() => {
    const root = document.documentElement
    if (!showFirstLayer) {
      root.style.removeProperty('--v3-cookie-bar-h')
      return
    }
    const bar = document.querySelector<HTMLElement>('[data-cookie-notice="bar"]')
    if (!bar) return
    const apply = () => {
      const height = Math.ceil(bar.getBoundingClientRect().height)
      root.style.setProperty('--v3-cookie-bar-h', `${height}px`)
    }
    apply()
    if (typeof ResizeObserver === 'undefined') {
      return () => root.style.removeProperty('--v3-cookie-bar-h')
    }
    const observer = new ResizeObserver(apply)
    observer.observe(bar)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--v3-cookie-bar-h')
    }
  }, [showFirstLayer])

  // The bar's top edge stays under any control marked data-cookie-bar-clear
  // (the search field), so it never takes a tap meant for it (cookie-bar-fit.ts).
  useEffect(() => {
    if (!showFirstLayer) return
    const bar = document.querySelector<HTMLElement>('[data-cookie-notice="bar"]')
    if (!bar) return
    const root = document.documentElement
    let frame = 0
    const fit = () => {
      frame = 0
      const keepClear = Array.from(document.querySelectorAll<HTMLElement>(`[${COOKIE_BAR_CLEAR_ATTR}]`), (el) =>
        el.getBoundingClientRect(),
      )
      const cap = cookieBarMaxHeight({
        viewportHeight: window.innerHeight,
        keepClear,
        stackedAbove: rootPx(root.style.getPropertyValue('--rr-dock-h')) + rootPx(root.style.getPropertyValue('--rr-sticky-bottom')),
      })
      bar.style.maxHeight = cap == null ? '' : `${cap}px`
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(fit)
    }
    fit()
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, { passive: true })
    // Late layout above the field (a lede, a font) grows the body and moves it.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    observer?.observe(document.body)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule)
      bar.style.maxHeight = ''
    }
  }, [showFirstLayer, pathname])

  if (!showFirstLayer && !prefsOpen && !showIcon) return null

  return (
    <>
    <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
      <DialogContent
        className="z-[120] bg-card sm:max-w-md max-h-[min(32rem,calc(100svh-2rem))] overflow-y-auto"
        overlayClassName="z-[120]"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>{CHOICES_PANEL.title}</DialogTitle>
        </DialogHeader>
        <PrivacyChoicesPanel
          analytics={analytics}
          marketing={marketing}
          gpc={gpcOn}
          onAnalyticsChange={(next) => {
            setAnalytics(next)
            // Clarity stops now, not on Save (ClarityRecorder).
            if (!next) window.dispatchEvent(new CustomEvent(CLARITY_ANALYTICS_OFF_EVENT))
          }}
          onMarketingChange={setMarketing}
          onSave={() => answer({ analytics, marketing })}
        />
      </DialogContent>
    </Dialog>
    {showIcon ? (
      <Button
        type="button"
        variant="secondary"
        size="icon"
        aria-label="Cookie settings"
        ref={cogRef}
        data-cookie-settings="icon"
        className="fixed z-[90] min-h-11 min-w-11 transition-none hidden"
        style={{
          transform: cogLiftPx ? `translateY(-${cogLiftPx}px)` : undefined,
          visibility: cogLiftPx === null ? 'hidden' : undefined,
          left: 'max(0.75rem, env(safe-area-inset-left, 0px))',
          bottom:
            'max(0.75rem, calc(var(--rr-dock-h, 0px) + var(--rr-sticky-bottom, 0px) + var(--rr-install-h, 0px) + env(safe-area-inset-bottom, 0px)))',
        }}
        onClick={openChooser}
      >
        <HugeiconsIcon icon={Settings01Icon} strokeWidth={2} />
      </Button>
    ) : null}
    {showFirstLayer ? (
    /* role="region", not role="dialog": non-modal bottom bar / sheet.
       Scroll does not record consent; only the buttons do.
       z-90: above the z-80 bottom docks so those docks cannot cover Decline. */
    <div
      role="region"
      aria-label="Cookie notice"
      data-cookie-notice="bar"
      data-consent-region={restricted ? 'restricted' : 'us'}
      className="fixed bottom-0 left-0 right-0 z-[90] max-h-[85svh] overflow-y-auto rounded-t-xl border-t border-border bg-card px-4 pt-3 shadow-md sm:rounded-none sm:px-6"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <div className="mx-auto max-w-3xl">
        <p className="text-sm font-medium text-foreground sm:text-base">{copy.heading}</p>
        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{copy.body}</p>
        <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{copy.second}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          <Link href="/privacy" className="font-medium text-foreground underline hover:no-underline">Privacy and cookies</Link>
          {' · '}
          <Link href="/privacy-choices" className="font-medium text-foreground underline hover:no-underline">Your privacy choices</Link>
        </p>
        {/* Pinned to the bar's foot: when the bar stands short of its words
            (cookie-bar-fit.ts, or past 85svh) the text scrolls and the two
            answers stay in view. No X: the bar stays until a button is chosen. */}
        <div className="sticky bottom-0 mt-3 grid grid-cols-2 items-stretch gap-2 bg-card">
          <Button
            type="button"
            variant={acceptVariant}
            className={CONSENT_CHOICE_BUTTON_CLASS}
            data-consent-action="accept"
            onClick={() => answer({ analytics: true, marketing: false })}
          >
            {copy.accept}
          </Button>
          <Button
            type="button"
            variant={declineVariant}
            className={CONSENT_CHOICE_BUTTON_CLASS}
            data-consent-action="decline"
            onClick={() => answer({ analytics: false, marketing: false })}
          >
            {copy.decline}
          </Button>
        </div>
        <Button
          type="button"
          variant="link"
          className="mt-1 h-auto px-0"
          data-consent-action="choices"
          onClick={openChooser}
        >
          {copy.choices}
        </Button>
      </div>
    </div>
    ) : null}
    </>
  )
}
